// ============================================================
// useApi — Typed fetch wrappers for all backend API endpoints
// ============================================================
import type {
  Race, ShipClass, Ship, ShipDetail,
  MarketGroup, MarketItem, Item, ItemDetail, Charge,
  FitDetail, FitModule, FitStats,
  BrowseFit, BrowseFitsResponse, PersonalFit, PersonalFitsResponse,
  SessionInfo, PriceInfo, FighterType,
  ApiResponse,
  SlotType, SubsystemSlot, ModuleState,
} from '../../shared/types';

// --- Toast system (simple event-based) ---

export type ToastType = 'info' | 'success' | 'error' | 'warning';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

let toastId = 0;
const toastListeners: Array<(toast: Toast) => void> = [];

export function onToast(listener: (toast: Toast) => void): () => void {
  toastListeners.push(listener);
  return () => {
    const idx = toastListeners.indexOf(listener);
    if (idx >= 0) toastListeners.splice(idx, 1);
  };
}

function showToast(type: ToastType, message: string): void {
  const toast: Toast = { id: ++toastId, type, message };
  for (const listener of toastListeners) {
    listener(toast);
  }
}

// --- Per-fit anonymous edit credentials ---

const FIT_TOKEN_PREFIX = 'evefit:edit-token:';

export function getFitEditToken(fitID: number): string | null {
  try { return localStorage.getItem(`${FIT_TOKEN_PREFIX}${fitID}`); } catch { return null; }
}

export function rememberFitEditToken(fitID: number, editToken?: string | null): void {
  if (!editToken || !/^[A-Za-z0-9_-]{43}$/.test(editToken)) return;
  try { localStorage.setItem(`${FIT_TOKEN_PREFIX}${fitID}`, editToken); } catch { /* storage unavailable */ }
}

function fitTokenHeaders(fitID?: number): Record<string, string> {
  if (!fitID) return {};
  const token = getFitEditToken(fitID);
  return token ? { 'X-EveFit-Edit-Token': token } : {};
}

// --- Core fetch helper ---

async function apiFetch<T>(url: string, options?: RequestInit, fitID?: number): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...fitTokenHeaders(fitID),
      ...options?.headers,
    },
  });

  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body.error) errorMsg = body.error;
    } catch { /* ignore parse errors */ }
    showToast('error', errorMsg);
    throw new Error(errorMsg);
  }

  const data = await res.json();

  // Handle ApiResponse wrapper if present (must have both success AND data fields)
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    const resp = data as ApiResponse<T>;
    if (!resp.success) {
      const msg = 'error' in resp ? resp.error : 'Unknown error';
      showToast('error', msg);
      throw new Error(msg);
    }
    return (resp as { success: true; data: T }).data;
  }

  // Handle error responses that have success: false but no data wrapper
  if (data && typeof data === 'object' && 'success' in data && !data.success) {
    const msg = 'error' in data ? (data as { error: string }).error : 'Unknown error';
    showToast('error', msg);
    throw new Error(msg);
  }

  return data as T;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') parts.push(`${k}=${encodeURIComponent(v)}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

// --- Ships API ---

export async function fetchRaces(): Promise<Race[]> {
  return apiFetch<Race[]>('/api/ships' + qs({ action: 'races' }));
}

export async function fetchShipClasses(raceID?: number | null): Promise<ShipClass[]> {
  return apiFetch<ShipClass[]>('/api/ships' + qs({ action: 'classes', raceID: raceID ?? undefined }));
}

export async function fetchShips(groupID: number, raceID?: number | null): Promise<Ship[]> {
  return apiFetch<Ship[]>('/api/ships' + qs({ action: 'list', groupID, raceID: raceID ?? undefined }));
}

export async function fetchShipDetail(typeID: number): Promise<ShipDetail> {
  return apiFetch<ShipDetail>('/api/ships' + qs({ action: 'get', typeID }));
}

// --- Items API ---

export async function fetchMarketTree(parentID?: number | null): Promise<MarketGroup[]> {
  return apiFetch<MarketGroup[]>('/api/items' + qs({
    action: 'market_tree',
    parentID: parentID ?? undefined,
  }));
}

export async function fetchItemsByMarketGroup(marketGroupID: number): Promise<MarketItem[]> {
  return apiFetch<MarketItem[]>('/api/items' + qs({ action: 'by_market_group', marketGroupID }));
}

export async function fetchItemsByGroup(groupID: number): Promise<Item[]> {
  return apiFetch<Item[]>('/api/items' + qs({ action: 'by_group', groupID }));
}

export async function searchItems(q: string, categoryID?: number): Promise<Item[]> {
  return apiFetch<Item[]>('/api/items' + qs({ action: 'search', q, categoryID }));
}

export async function fetchItemDetail(typeID: number): Promise<ItemDetail> {
  return apiFetch<ItemDetail>('/api/items' + qs({ action: 'get', typeID }));
}

export async function fetchModulesForSlot(slotType: SlotType, shipTypeID?: number): Promise<Item[]> {
  return apiFetch<Item[]>('/api/items' + qs({ action: 'modules_for_slot', slotType, shipTypeID }));
}

export async function fetchChargesForModule(typeID: number): Promise<Charge[]> {
  return apiFetch<Charge[]>('/api/items' + qs({ action: 'charges_for_module', moduleTypeID: typeID }));
}

export async function fetchFighters(): Promise<FighterType[]> {
  return apiFetch<FighterType[]>('/api/items' + qs({ action: 'fighters' }));
}

export async function fetchImplantsForSlot(slot: number): Promise<Item[]> {
  return apiFetch<Item[]>('/api/items' + qs({ action: 'implants_for_slot', slot }));
}

export async function fetchSubsystemsForSlot(slot: SubsystemSlot, shipName: string): Promise<Item[]> {
  return apiFetch<Item[]>('/api/items' + qs({ action: 'subsystems_for_slot', slot, shipName }));
}

export async function fetchSubsystemAttributes(typeID: number): Promise<Record<string, number>> {
  return apiFetch<Record<string, number>>('/api/items' + qs({ action: 'subsystem_attributes', typeID }));
}

// --- Fits API ---

export async function fetchFit(fitID: number): Promise<FitDetail> {
  return apiFetch<FitDetail>('/api/fits' + qs({ action: 'get', fitID }), undefined, fitID);
}

export async function createFit(shipTypeID: number, fitName: string): Promise<{ fitID: number; editToken?: string }> {
  return apiFetch<{ fitID: number; editToken?: string }>('/api/fits' + qs({ action: 'create' }), {
    method: 'POST',
    body: JSON.stringify({ shipTypeID, fitName }),
  });
}

export async function addModule(fitID: number, typeID: number, slotType: SlotType, slotPosition: number, state?: ModuleState): Promise<FitModule> {
  return apiFetch<FitModule>('/api/fits' + qs({ action: 'add_module' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID, slotType, slotPosition, state }),
  }, fitID);
}

export async function removeModule(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'module', id }), { method: 'DELETE' }, fitID);
}

export async function addDrone(fitID: number, typeID: number, quantity: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'add_drone' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID, quantity }),
  }, fitID);
}

export async function removeDrone(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'drone', id }), { method: 'DELETE' }, fitID);
}

export async function updateDrone(id: number, activeCount: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'update_drone' }), {
    method: 'POST',
    body: JSON.stringify({ id, activeCount }),
  }, fitID);
}

export async function addCargo(fitID: number, typeID: number, quantity: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'add_cargo' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID, quantity }),
  }, fitID);
}

export async function removeCargo(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'cargo', id }), { method: 'DELETE' }, fitID);
}

export async function addImplant(fitID: number, typeID: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'add_implant' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID }),
  }, fitID);
}

export async function removeImplant(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'implant', id }), { method: 'DELETE' }, fitID);
}

export async function addSubsystem(fitID: number, typeID: number, slot: SubsystemSlot): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'add_subsystem' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID, slot }),
  }, fitID);
}

export async function removeSubsystem(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'subsystem', id }), { method: 'DELETE' }, fitID);
}

export async function addFighter(fitID: number, typeID: number, tubeIndex: number, squadronSize: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'add_fighter' }), {
    method: 'POST',
    body: JSON.stringify({ fitID, typeID, tubeIndex, squadronSize }),
  }, fitID);
}

export async function removeFighter(id: number, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'fighter', id }), { method: 'DELETE' }, fitID);
}

export async function updateFighter(id: number, data: { isActive?: boolean; squadronSize?: number }, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'update_fighter' }), {
    method: 'POST',
    body: JSON.stringify({ id, ...data }),
  }, fitID);
}

export async function setModuleState(moduleId: number, state: ModuleState, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'set_module_state' }), {
    method: 'POST',
    body: JSON.stringify({ moduleID: moduleId, state }),
  }, fitID);
}

export async function setCharge(moduleId: number, chargeTypeID: number | null, fitID?: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'set_charge' }), {
    method: 'POST',
    body: JSON.stringify({ moduleID: moduleId, chargeTypeID }),
  }, fitID);
}

export async function updateFit(fitID: number, fitName?: string, notes?: string): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'update' }), {
    method: 'PUT',
    body: JSON.stringify({ fitID, fitName, notes }),
  }, fitID);
}

export async function saveFit(fitID: number, fitName?: string): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'save' }), {
    method: 'PUT',
    body: JSON.stringify({ fitID, fitName }),
  }, fitID);
}

export async function deleteFit(fitID: number): Promise<void> {
  await apiFetch<void>('/api/fits' + qs({ action: 'fit', fitID }), { method: 'DELETE' }, fitID);
}

export async function copyFit(fitID: number, newName?: string): Promise<{ fitID: number; editToken?: string }> {
  return apiFetch<{ fitID: number; editToken?: string }>('/api/fits' + qs({ action: 'copy' }), {
    method: 'POST',
    body: JSON.stringify({ sourceFitID: fitID, newName }),
  }, fitID);
}

export async function importEft(eft: string): Promise<{ fitID: number; editToken?: string }> {
  return apiFetch<{ fitID: number; editToken?: string }>('/api/fits' + qs({ action: 'import_eft' }), {
    method: 'POST',
    body: JSON.stringify({ eft }),
  });
}

export async function browseFits(
  page?: number, classID?: number, shipTypeID?: number, search?: string,
  sortBy?: string, sortOrder?: string, minPrice?: number, maxPrice?: number,
): Promise<BrowseFitsResponse> {
  return apiFetch<BrowseFitsResponse>('/api/fits' + qs({
    action: 'browse', page, classID, shipTypeID, search,
    sortBy, sortOrder, minPrice, maxPrice,
  }));
}

export async function fetchFitShipClasses(): Promise<ShipClass[]> {
  return apiFetch<ShipClass[]>('/api/fits' + qs({ action: 'ship_classes' }));
}

export async function fetchFitHulls(classID: number): Promise<Ship[]> {
  return apiFetch<Ship[]>('/api/fits' + qs({ action: 'hulls', classID }));
}

// --- Calculate API ---

export async function calculateFit(fitID: number, allSkillsLevel?: number): Promise<FitStats> {
  return apiFetch<FitStats>('/api/calculate' + qs({ fitID, allSkillsLevel }), undefined, fitID);
}

// --- Prices API ---

export async function fetchPrices(typeIDs: number[]): Promise<Record<string, PriceInfo>> {
  return apiFetch<Record<string, PriceInfo>>('/api/prices' + qs({
    action: 'get',
    typeIDs: typeIDs.join(','),
  }));
}

// --- Auth API ---

export async function fetchSession(): Promise<SessionInfo> {
  return apiFetch<SessionInfo>('/api/auth' + qs({ action: 'status' }));
}

export async function getLoginUrl(): Promise<string> {
  const data = await apiFetch<{ url: string }>('/api/auth' + qs({ action: 'login' }));
  return data.url;
}

export async function logout(): Promise<void> {
  await apiFetch<void>('/api/auth' + qs({ action: 'logout' }));
}

export async function browsePersonalFits(
  page?: number, classID?: number, shipTypeID?: number, search?: string,
): Promise<PersonalFitsResponse> {
  return apiFetch<PersonalFitsResponse>('/api/auth' + qs({
    action: 'browse_personal', page, classID, shipTypeID, search,
  }));
}

export async function syncPersonalFittings(): Promise<void> {
  await apiFetch<void>('/api/auth' + qs({ action: 'sync_personal_fittings' }), {
    method: 'POST',
  });
}

export async function fetchPersonalShipClasses(): Promise<ShipClass[]> {
  return apiFetch<ShipClass[]>('/api/auth' + qs({ action: 'my_ship_classes' }));
}

export async function fetchPersonalHulls(classID: number): Promise<Ship[]> {
  return apiFetch<Ship[]>('/api/auth' + qs({ action: 'my_hulls', classID }));
}

export async function exportToEve(fitID: number): Promise<void> {
  await apiFetch<void>('/api/auth' + qs({ action: 'export_to_eve' }), {
    method: 'POST',
    body: JSON.stringify({ fitID }),
  }, fitID);
}

export async function importEveFit(fitting: Record<string, unknown>): Promise<{ fitID: number }> {
  return apiFetch<{ fitID: number }>('/api/auth' + qs({ action: 'import_eve_fit' }), {
    method: 'POST',
    body: JSON.stringify({ fitting }),
  });
}

export async function getPersonalFittings(): Promise<PersonalFit[]> {
  return apiFetch<PersonalFit[]>('/api/auth' + qs({ action: 'get_personal_fittings' }));
}

// --- Icons ---

export function iconUrl(typeID: number, size: 32 | 64 | 128 = 64): string {
  return `/api/icons?type=${typeID}&size=${size}`;
}
